import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ArtistPortalEarningsGroup, ArtistPortalEarningsRow } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../../common/types';
import { MoneyAmount } from './MoneyAmount';
import { earningsRowLabel, rowsHaveUnits } from '../earningsRows';
import { formatStreamingNumber } from '../streamingFormat';
import { streamingEarningsMessages as messages } from '../streamingEarningsMessages';
import { streamingBlockMessages } from '../streamingBlockMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EarningsRowsTableProps {
  /** The first column: "Store", "Track" or "Country". */
  nameLabel: string;
  /** `country`: the label is an ISO code, the cell shows the country name. */
  groupBy: ArtistPortalEarningsGroup;
  rows: ArtistPortalEarningsRow[];
  currency: string | null;
}

/**
 * "By store", "By track" or "By country" of one currency (design e1): the units, gross, net and the
 * share of the net. Units: a 0 is a number, a missing value is a dash, and the column shows only
 * when a row gives units.
 */
const EarningsRowsTableComponent: React.FC<EarningsRowsTableProps> = ({ nameLabel, groupBy, rows, currency }) => {
  const intl = useIntl();
  const sum = useMemo(() => rows.reduce((acc, row) => acc + (row.netAmount ?? 0), 0), [rows]);
  const showUnits = useMemo(() => rowsHaveUnits(rows), [rows]);
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>{nameLabel}</th>
          {showUnits ? <th className={styles.numCell}>{intl.formatMessage(messages.units)}</th> : null}
          <th className={styles.numCell}>{intl.formatMessage(messages.colGross)}</th>
          <th className={styles.numCell}>{intl.formatMessage(messages.colNet)}</th>
          <th className={styles.numCell}>{intl.formatMessage(streamingBlockMessages.colShare)}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.label}:${row.trackExternalId ?? ''}`}>
            <td>{earningsRowLabel(intl, groupBy, row.label)}</td>
            {showUnits ? <td className={styles.numCell}>{formatStreamingNumber(intl, row.quantity)}</td> : null}
            <td className={styles.numCell}>
              <MoneyAmount value={row.grossAmount} currency={currency} />
            </td>
            <td className={styles.numCell}>
              <MoneyAmount value={row.netAmount} currency={currency} />
            </td>
            <td className={styles.numCell}>
              {sum > 0 && row.netAmount !== null
                ? intl.formatNumber(row.netAmount / sum, { style: 'percent', maximumFractionDigits: 1 })
                : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const EarningsRowsTable = TypedMemo(EarningsRowsTableComponent);
